/**
 * Build schedule grid data from event instances.
 *
 * Groups volunteer task instances by team, then by task, with a Map of
 * event-instance-id -> cell data (assignees etc.) per task.
 *
 * @param {Array} instances — event instances with nested TaskInstances
 * @returns {Array<{teamName, volunteerTeamID, assigners, tasks: Array<{task, volunteerTeamID, teamName, instances: Map}>}>}
 */
export function buildScheduleGridData(instances) {
    if (!instances?.length) return [];

    const tasksMap = new Map();
    instances.forEach(inst => {
        inst.TaskInstances?.forEach(ti => {
            if (ti.Task?.dueType !== 'EVENT_VOLUNTEER') return;
            const task = ti.Task;
            const volunteerTeamID = ti.volunteerTeamID
                || task.VolunteerTeamTasks?.[0]?.volunteerTeamID
                || null;
            const mapKey = `${task.id}:${volunteerTeamID || 'none'}`;
            const teamName = ti.VolunteerTeam?.name
                || task.VolunteerTeamTasks?.find(vtt => vtt.volunteerTeamID === volunteerTeamID)?.VolunteerTeam?.name
                || task.VolunteerTeamTasks?.[0]?.VolunteerTeam?.name
                || 'Uncategorized';
            const teamRoleID = ti.VolunteerTeam?.roleID
                || task.VolunteerTeamTasks?.find(vtt => vtt.volunteerTeamID === volunteerTeamID)?.VolunteerTeam?.roleID
                || task.VolunteerTeamTasks?.[0]?.VolunteerTeam?.roleID
                || null;
            if (!tasksMap.has(mapKey)) {
                tasksMap.set(mapKey, {
                    task,
                    volunteerTeamID,
                    teamName,
                    teamRoleID,
                    instances: new Map(),
                });
            }
            const cell = tasksMap.get(mapKey).instances.get(inst.id);
            if (cell) {
                // The same task+team produced more than one TaskInstance for a
                // single event occurrence. Keep every instance ID and flag the
                // cell so renderers mark it instead of silently picking one.
                cell.taskInstanceIDs = [...(cell.taskInstanceIDs || [cell.taskInstanceID]), ti.id];
                cell.assignees = [...cell.assignees, ...(ti.Assignees || [])];
                cell.multipleInstances = true;
            } else {
                tasksMap.get(mapKey).instances.set(inst.id, {
                    taskInstanceID: ti.id,
                    taskInstanceIDs: [ti.id],
                    assignees: ti.Assignees || [],
                    eventInstance: inst,
                    taskInstance: { ...ti, EventInstance: inst },
                    multipleInstances: false,
                });
            }
        });
    });

    // Group by team ID — distinct teams can share a display name.
    const teamsMap = new Map();
    tasksMap.forEach((taskData) => {
        const teamKey = taskData.volunteerTeamID ?? `name:${taskData.teamName}`;
        if (!teamsMap.has(teamKey)) {
            teamsMap.set(teamKey, {
                teamName: taskData.teamName,
                volunteerTeamID: taskData.volunteerTeamID,
                teamRoleID: taskData.teamRoleID,
                assigners: taskData.task?.VolunteerTeamTasks?.find(vtt => vtt.volunteerTeamID === taskData.volunteerTeamID)?.VolunteerTeam?.VolunteerTeamAssigners || [],
                tasks: [],
            });
        }
        teamsMap.get(teamKey).tasks.push(taskData);
    });

    const result = Array.from(teamsMap.values())
        .sort((a, b) => a.teamName.localeCompare(b.teamName));
    const getTaskPosition = (task, volunteerTeamID) => {
        const vtt = task.VolunteerTeamTasks?.find(v => v.volunteerTeamID === volunteerTeamID);
        return vtt?.position ?? 0;
    };
    result.forEach(team => {
        team.tasks.sort((a, b) => getTaskPosition(a.task, a.volunteerTeamID) - getTaskPosition(b.task, b.volunteerTeamID) || a.task.title.localeCompare(b.task.title));
    });

    return result;
}
